import { useMemo, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useGetDataQuery, portalApi, fetchBackgroundData } from '../redux/api/portalApi';
import { resourceForUrl } from './resources';
import { useRealtimeResource, useRealtimeState } from './RealtimeProvider';

const EMPTY = Object.freeze([]);
export function readList(value) {
  const rows = Array.isArray(value) ? value : Array.isArray(value?.value) ? value.value
    : Array.isArray(value?.data) ? value.data
      : value?.data && typeof value.data === 'object' ? Object.values(value.data) : undefined;
  return rows?.every(row => row && typeof row === 'object' && !Array.isArray(row)) ? rows : undefined;
}

// Read-only list owners only. Editable query consumers must report draft state
// before adopting background refetches, which update RTK's cache immediately.
export default function useRealtimeReadOnlyList({ url, params, contract, live = true }) {
  const store = useStore();
  const user = useSelector(state => state.user?.userDetails);
  const baseUrl = useSelector(state => state.apiUrls?.urls?.generalProjectInfoEnhanced);
  const { mode } = useRealtimeState();
  let resource = null;
  try { resource = resourceForUrl(url, new URL('.', baseUrl).href); } catch { /* Unconfigured API. */ }
  const args = { url, ...(params ? { params } : {}), ...(contract ? { contract } : {}) };
  const query = useGetDataQuery(args, { skip: !url, refetchOnMountOrArgChange: true });
  const key = JSON.stringify([user?.localAccountId || user?.username, url, params, contract]);
  const lastGood = useRef({ key, data: undefined });
  if (lastGood.current.key !== key) lastGood.current = { key, data: undefined };
  const rows = useMemo(() => readList(query.currentData), [query.currentData]);
  const malformed = query.currentData !== undefined && rows === undefined;
  if (rows !== undefined) lastGood.current.data = rows;
  const currentKey = useRef(key);
  currentKey.current = key;
  useRealtimeResource({
    enabled: Boolean(live && url && resource && ['domain', 'session'].includes(mode)),
    ownerKey: `readonly-list:${key}`,
    resources: resource ? [resource] : [],
    blocked: query.isFetching,
    refresh: async ({ signal }) => {
      if (signal.aborted || currentKey.current !== key) return false;
      const before = portalApi.endpoints.getData.select(args)(store.getState());
      const data = await store.dispatch(fetchBackgroundData({ url, params, contract: contract || {}, signal }));
      if (signal.aborted || currentKey.current !== key || readList(data) === undefined) return false;
      const after = portalApi.endpoints.getData.select(args)(store.getState());
      if (after.status === 'pending' || after.requestId !== before.requestId) return false;
      if (after.status === 'rejected' || after.data === undefined) {
        await store.dispatch(portalApi.util.upsertQueryData('getData', args, data));
      } else {
        store.dispatch(portalApi.util.updateQueryData('getData', args, () => data));
      }
      return true;
    },
  });
  return {
    ...query,
    data: lastGood.current.data || EMPTY,
    error: query.error || (malformed ? { message: 'Invalid list response; showing the last loaded data.' } : undefined),
    isLoading: query.isLoading && lastGood.current.data === undefined,
  };
}
